import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import StaffNavBar from "../components/StaffNavBar";
import {
  AdminPageBody,
  AdminPanel,
  AdminLoadingState,
  adminButton,
  adminPrimaryButton,
  adminDangerButton,
} from "../components/AdminPageShell";
import { useStaffContent } from "../store/staff";

const CONTENT_TYPES = [
  { value: "books", label: "Books" },
  { value: "courses", label: "Courses" },
  { value: "journals", label: "Journals" },
  { value: "learning-paths", label: "Learning paths" },
];
const BASE_STATUSES = ["PENDING", "APPROVED", "REJECTED", "DRAFT", "DISABLED"];
const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";

const itemTitle = (item) => item.title || item.name || "Untitled";
const itemAuthor = (item) =>
  item.author?.name || item.authorName || item.resourcePerson?.name || item.author || "—";

export default function StaffContentReview() {
  const {
    staffContentItems,
    staffContentLoading,
    staffContentError,
    staffContentTotal,
    staffContentPages,
    staffContentDetailLoading,
    staffContentDetailError,
    staffContentMutationLoading,
    staffContentMutationError,
    fetchStaffContent,
    clearStaffContent,
    fetchStaffContentDetail,
    updateStaffContent,
    approveStaffContent,
    rejectStaffContent,
    disableStaffCourse,
    featureStaffCourse,
    unfeatureStaffCourse,
  } = useStaffContent();
  const [searchParams] = useSearchParams();
  const initialType = CONTENT_TYPES.some((entry) => entry.value === searchParams.get("type"))
    ? searchParams.get("type")
    : "";
  const [type, setType] = useState(initialType);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("PENDING");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [rejectReason, setRejectReason] = useState("");
  const [message, setMessage] = useState("");
  const [loadingAll, setLoadingAll] = useState(true);

  const loadContent = useCallback(async () => {
    setLoadingAll(true);
    clearStaffContent();
    const selectedTypes = type ? [type] : CONTENT_TYPES.map((entry) => entry.value);
    try {
      await Promise.all(
        selectedTypes.map((contentType) =>
          fetchStaffContent(contentType, {
            page,
            limit: 10,
            submitted: true,
            sort: "newest",
            ...(search ? { search } : {}),
            ...(status ? { status } : {}),
          })
        )
      );
    } finally {
      setLoadingAll(false);
    }
  }, [clearStaffContent, fetchStaffContent, page, search, status, type]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const openDetail = async (item) => {
    setSelected({ ...item, contentType: item.contentType });
    const result = await fetchStaffContentDetail(item.contentType, item.id);
    if (result.success) {
      const detail = { ...item, ...result.data, contentType: item.contentType };
      setSelected(detail);
      setForm({
        title: detail.title || "",
        author: detail.author?.name || detail.author || detail.authorName || "",
        description: detail.description || "",
        price: detail.price ?? "",
        coverImage: detail.coverImage || detail.cover_image || "",
        fileUrl: detail.fileUrl || detail.file_url || "",
        categoryId: detail.categoryId || detail.category?.id || "",
      });
    }
  };

  const runAction = async (action, successText) => {
    const result = await action();
    if (!result.success) return;
    setMessage(successText);
    setSelected(null);
    await loadContent();
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    const fieldsByType = {
      books: ["title", "author", "price", "coverImage", "fileUrl"],
      courses: ["title", "description", "price", "coverImage", "categoryId"],
      journals: ["title", "author", "price", "fileUrl"],
      "learning-paths": ["title", "description", "coverImage", "price"],
    };
    // The edit schemas are strict and URL/number fields reject empty strings,
    // so only send fields the staff member actually filled in.
    const payload = {};
    fieldsByType[selected.contentType].forEach((key) => {
      const value = form[key];
      if (value === undefined || value === null || value === "") return;
      if (key === "price") {
        const price = Number(value);
        if (!Number.isNaN(price)) payload.price = price;
        return;
      }
      if (typeof value === "string" && !value.trim()) return;
      payload[key] = typeof value === "string" ? value.trim() : value;
    });
    await runAction(
      () => updateStaffContent(selected.contentType, selected.id, payload),
      "Content updated."
    );
  };

  const handleModerate = async (kind) => {
    if (kind === "reject" && !rejectReason.trim()) return;
    await runAction(
      () =>
        kind === "approve"
          ? approveStaffContent(selected.contentType, selected.id)
          : rejectStaffContent(selected.contentType, selected.id, rejectReason.trim()),
      kind === "approve" ? "Content approved." : "Content rejected."
    );
    setRejectReason("");
  };

  const handleCourseAction = async (action) => {
    const actions = {
      disable: () => disableStaffCourse(selected.id),
      feature: () => featureStaffCourse(selected.id),
      unfeature: () => unfeatureStaffCourse(selected.id),
    };
    const messages = {
      disable: "Course disabled.",
      feature: "Course featured.",
      unfeature: "Course unfeatured.",
    };
    await runAction(actions[action], messages[action]);
  };

  const rows = staffContentItems.filter(
    (item) => !type || item.contentType === type
  );

  // Only books and journals have a WITHDRAWN status; sending it for courses or
  // learning paths would fail the strict status enum.
  const statusOptions =
    type === "books" || type === "journals"
      ? [...BASE_STATUSES, "WITHDRAWN"]
      : BASE_STATUSES;

  return (
    <div>
      <StaffNavBar
        title="Content Review"
        context="Review and manage books, courses, journals, and learning paths."
      />
      <AdminPageBody>
        <AdminPanel className="overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
            <input
              className={`${inputClass} max-w-xs`}
              placeholder="Search content"
              value={search}
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
            />
            <select
              className={`${inputClass} max-w-[190px]`}
              value={type}
              onChange={(event) => {
                const nextType = event.target.value;
                setPage(1);
                setType(nextType);
                if (
                  status === "WITHDRAWN" &&
                  nextType !== "books" &&
                  nextType !== "journals"
                ) {
                  setStatus("");
                }
              }}
            >
              <option value="">All content types</option>
              {CONTENT_TYPES.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
            <select
              className={`${inputClass} max-w-[170px]`}
              value={status}
              onChange={(event) => {
                setPage(1);
                setStatus(event.target.value);
              }}
            >
              <option value="">All statuses</option>
              {statusOptions.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <button className={`${adminButton} ml-auto`} onClick={loadContent}>
              Refresh
            </button>
          </div>
          {staffContentError && (
            <div className="border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {staffContentError}
            </div>
          )}
          {staffContentMutationError && (
            <div className="border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {staffContentMutationError}
            </div>
          )}
          {message && (
            <div className="border-b border-green-100 bg-green-50 px-4 py-3 text-sm text-green-700">
              {message}
              <button className="ml-3 underline" onClick={() => setMessage("")}>Dismiss</button>
            </div>
          )}
          {staffContentLoading || loadingAll ? (
            <AdminLoadingState />
          ) : rows.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-slate-500">
              No content found for these filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Title</th>
                    <th className="px-5 py-3">Type</th>
                    <th className="px-5 py-3">Author</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3 text-right">Price</th>
                    <th className="px-5 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((item) => (
                    <tr key={`${item.contentType}-${item.id}`}>
                      <td className="px-5 py-3 font-medium text-slate-900">{itemTitle(item)}</td>
                      <td className="px-5 py-3 text-slate-600">
                        {CONTENT_TYPES.find((entry) => entry.value === item.contentType)?.label}
                      </td>
                      <td className="px-5 py-3 text-slate-600">{itemAuthor(item)}</td>
                      <td className="px-5 py-3 text-slate-600">{item.status || "—"}</td>
                      <td className="px-5 py-3 text-right text-slate-600">
                        {item.price == null ? "—" : `₦${Number(item.price).toLocaleString()}`}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <button className={adminButton} onClick={() => openDetail(item)}>
                          Review / edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-slate-200 p-4 text-sm text-slate-500">
            <span>Page {page} of {staffContentPages} · {staffContentTotal} results</span>
            <div className="flex gap-2">
              <button className={adminButton} disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>
                Previous
              </button>
              <button
                className={adminButton}
                disabled={page >= staffContentPages}
                onClick={() => setPage((n) => n + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </AdminPanel>
      </AdminPageBody>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
          <section className="my-auto w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="m-0 text-lg font-semibold text-slate-900">
                {itemTitle(selected)}
              </h2>
              <button className={adminButton} onClick={() => setSelected(null)}>Close</button>
            </div>
            {staffContentDetailLoading ? (
              <AdminLoadingState />
            ) : staffContentDetailError ? (
              <p className="text-sm text-red-700">{staffContentDetailError}</p>
            ) : (
              <>
                <form className="grid gap-3 sm:grid-cols-2" onSubmit={handleUpdate}>
                  {Object.entries(form).map(([key, value]) => (
                    <label key={key} className="text-xs font-semibold capitalize text-slate-600">
                      {key}
                      <input
                        className={`${inputClass} mt-1`}
                        type={key === "price" ? "number" : "text"}
                        value={value}
                        onChange={(event) =>
                          setForm((current) => ({ ...current, [key]: event.target.value }))
                        }
                      />
                    </label>
                  ))}
                  <button
                    className={`${adminPrimaryButton} sm:col-span-2`}
                    disabled={staffContentMutationLoading}
                  >
                    Save content edits
                  </button>
                </form>
                <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-200 pt-4">
                  <button
                    className={adminPrimaryButton}
                    disabled={staffContentMutationLoading}
                    onClick={() => handleModerate("approve")}
                  >
                    Approve
                  </button>
                  {selected.contentType === "courses" && (
                    <>
                      <button className={adminButton} disabled={staffContentMutationLoading} onClick={() => handleCourseAction("feature")}>Feature course</button>
                      <button className={adminButton} disabled={staffContentMutationLoading} onClick={() => handleCourseAction("unfeature")}>Unfeature course</button>
                      <button className={adminDangerButton} disabled={staffContentMutationLoading} onClick={() => handleCourseAction("disable")}>Disable course</button>
                    </>
                  )}
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  <label className="text-xs font-semibold text-slate-600" htmlFor="staff-content-reason">
                    Rejection reason
                  </label>
                  <textarea
                    id="staff-content-reason"
                    className={inputClass}
                    rows={3}
                    value={rejectReason}
                    onChange={(event) => setRejectReason(event.target.value)}
                  />
                  <button
                    className={adminDangerButton}
                    disabled={staffContentMutationLoading || !rejectReason.trim()}
                    onClick={() => handleModerate("reject")}
                  >
                    Reject content
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
